// Category taxonomy. The slug (`value`) is a cross-cutting contract: web onboarding
// writes it, the browse filter queries on it, and the DB stores it as Service.category.
// Labels and tag suggestions are presentation, co-located here to keep one source of truth.
//
// TODO: migrate this to a DB `Category` table (slug / label / icon / sort order / active
// flag, with Service referencing it) once we have a clearer picture of what we want —
// admin-managed categories, per-category metadata, and referential integrity. Deferred
// for now: a fixed table tensions with the free-form "Other" category path in service
// creation, so we keep the taxonomy in code until that's designed.

// keep slugs stable — seed data and existing services depend on them
export const CATEGORIES = [
  { value: 'moving', label: 'Moving' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'pet-care', label: 'Pet Care' },
  { value: 'tutoring', label: 'Tutoring' },
  { value: 'handyman', label: 'Handyman' },
  { value: 'gardening-landscaping', label: 'Gardening & Landscaping' },
  { value: 'photography', label: 'Photography' },
  { value: 'personal-training', label: 'Personal Training & Fitness' },
  { value: 'beauty-wellness', label: 'Beauty & Wellness' },
  { value: 'catering-events', label: 'Catering & Events' },
  { value: 'childcare', label: 'Childcare' },
  { value: 'delivery-errands', label: 'Delivery & Errands' },
] as const

export type CategorySlug = (typeof CATEGORIES)[number]['value']

// browse filter prepends the "All" pseudo-category; onboarding uses CATEGORIES directly
export const FILTER_CATEGORIES = [{ value: 'All', label: 'All' }, ...CATEGORIES] as const

// suggested tags per category slug, surfaced in the service onboarding tag step
export const TAG_SUGGESTIONS: Record<string, string[]> = {
  'moving': ['furniture', 'local', 'long-distance', 'same-day', 'packing', 'storage'],
  'cleaning': ['deep-clean', 'residential', 'commercial', 'move-out', 'windows', 'recurring'],
  'pet-care': ['dog walking', 'pet sitting', 'grooming', 'training', 'overnight'],
  'tutoring': ['math', 'science', 'test-prep', 'languages', 'music', 'online'],
  'handyman': ['plumbing', 'electrical', 'painting', 'assembly', 'repairs', 'installation'],
  'gardening-landscaping': ['lawn care', 'tree trimming', 'design', 'irrigation', 'seasonal'],
  'photography': ['portraits', 'events', 'real estate', 'product', 'wedding'],
  'personal-training': ['weight loss', 'strength', 'yoga', 'cardio', 'group', 'home visits'],
  'beauty-wellness': ['haircut', 'nails', 'makeup', 'massage', 'skincare', 'mobile'],
  'catering-events': ['weddings', 'corporate', 'private chef', 'meal prep', 'baking'],
  'childcare': ['babysitting', 'after-school', 'nanny', 'special needs'],
  'delivery-errands': ['grocery', 'packages', 'furniture', 'same-day', 'scheduled'],
}

const LABELS = new Map<string, string>(CATEGORIES.map((c) => [c.value, c.label]))

export function categoryLabel(value: string): string {
  return LABELS.get(value) ?? value
}
